import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { AlertTriangle, Info, LucideAngularModule, Megaphone, X } from 'lucide-angular';
import type { Announcement } from '../../models/announcement';

/**
 * The platform announcement strip.
 *
 * **Presentational only — this component never fetches and injects no service.** It takes an
 * announcement via `input()` and reports dismissal via `output()`. That is a hard constraint, not a
 * preference: PR 3's backoffice authoring form renders this same component as a live preview fed
 * from unsaved form values, which is impossible if the banner sources its own data. The polling,
 * gating and selection logic lives in `AnnouncementBannerContainer`.
 *
 * See `announcement-banner.md` for the rendering contract (plain text, no innerHTML).
 */
@Component({
  selector: 'app-announcement-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LucideAngularModule],
  templateUrl: './announcement-banner.html',
  styleUrl: './announcement-banner.css',
})
export class AnnouncementBanner {
  /** The announcement to show, or null to render nothing. */
  readonly announcement = input<Announcement | null>(null);

  /** Emitted when the user activates the dismiss control. */
  readonly dismiss = output<string>();

  readonly icons = { Info, AlertTriangle, Megaphone, X } as const;

  /**
   * Whole view-model in one `computed` — ALWAYS #16 / NEVER #16 forbid calling methods from Tier-1
   * template bindings, and a per-binding function would also re-run on every change-detection pass.
   */
  protected readonly vm = computed(() => {
    const announcement = this.announcement();
    if (!announcement) return null;

    const severity = announcement.severity;
    return {
      id: announcement.id,
      message: announcement.message,
      dismissible: announcement.dismissible,
      severity,
      /** Lowercased for the CSS modifier only — the wire value stays uppercase. */
      modifier: `announcement-banner--${severity.toLowerCase()}`,
      /**
       * INFO is passive information, so `status` announces it politely; WARNING and CRITICAL
       * interrupt, which is what `alert` is for.
       */
      role: severity === 'INFO' ? 'status' : 'alert',
      icon: severity === 'CRITICAL' || severity === 'WARNING' ? AlertTriangle : Info,
      dismissLabel: `Dismiss announcement: ${announcement.message}`,
    };
  });

  protected onDismiss(id: string): void {
    this.dismiss.emit(id);
  }
}
